import { MAX_MAGNETS, PHYSICS, WORLD_HEIGHT, WORLD_WIDTH } from '../config.ts';
import { worldBounds, type LevelDefinition } from '../levels/Level.ts';
import { Ball } from './Ball.ts';
import { collideCircle, type Collider } from './Collision.ts';
import { createBallProbe, Ferrofluid, type FluidBallProbe } from './Ferrofluid.ts';
import { Magnet, type MagnetSize, type Polarity } from './Magnet.ts';
import { MagneticField } from './MagneticField.ts';

/**
 * Where the level is in its cycle.
 *
 * The game is plan-then-run: magnets are placed with everything frozen, the run is
 * committed to as a whole, and it plays out without further input. That makes each attempt
 * a readable experiment — the board did what your field said it would — rather than a
 * continuous wrestle with a blob.
 */
export type Phase = 'planning' | 'running' | 'solved' | 'failed';

/** Something worth reacting to. Drained by the presentation layer every frame. */
export type WorldEvent =
  | { type: 'ballImpact'; x: number; y: number; speed: number }
  | { type: 'started' }
  | { type: 'solved'; x: number; y: number }
  | { type: 'failed'; x: number; y: number };

/** Below this impact speed a wall hit is a scuff, not a knock. */
const IMPACT_THRESHOLD = 90;

/** Seconds the ball must stay in a ball goal before it counts. */
const GOAL_DWELL = 0.25;

/** A run is abandoned after this long, however slowly things are still creeping. */
const RUN_TIMEOUT = 26;

/** Nothing is judged settled before this, since everything starts at rest. */
const SETTLE_GRACE = 2.0;

/** Mean speed below which the board counts as having stopped moving, in wu/s. */
const SETTLE_SPEED = 14;

/** How long it has to stay that slow. */
const SETTLE_TIME = 1.1;

/** How many spiral slots to try before giving up on placing a particle. */
const EMISSION_ATTEMPTS = 48;

/** The golden angle, in radians — see fillPoint. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** Headroom above the level's fluid budget, so the buffers never reallocate. */
const FLUID_HEADROOM = 64;

/** Deterministic PRNG — reproducible runs make "it did something weird" debuggable. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The simulation, assembled.
 *
 * World owns state and time; it knows nothing about Pixi, the DOM or input gestures. The
 * renderer reads from it, the input layer calls methods on it, and neither is allowed to
 * reach the other way round.
 */
export class World {
  level: LevelDefinition;

  readonly field = new MagneticField();
  /** Null on levels whose objective is the fluid itself. */
  ball: Ball | null = null;
  fluid: Ferrofluid;

  /** Level geometry plus the invisible world box. */
  colliders: Collider[] = [];

  phase: Phase = 'planning';

  private readonly probe: FluidBallProbe = createBallProbe();
  private readonly fillScratch = { x: 0, y: 0 };
  private random: () => number;

  /**
   * Things that happened this tick and that sound, effects and haptics want to know
   * about. The simulation raises them; it has no idea anyone is listening.
   */
  readonly events: WorldEvent[] = [];

  /** Seconds the current run has been going. Zero while planning. */
  elapsed = 0;
  /** Every placement this attempt, including magnets later removed. */
  magnetsUsed = 0;
  /** How full the goal is, 0..1. Drives the goal ring and the results card. */
  goalProgress = 0;

  private goalDwell = 0;
  private settleTime = 0;

  constructor(level: LevelDefinition) {
    this.level = level;
    this.fluid = new Ferrofluid(level.fluid.amount + FLUID_HEADROOM);
    this.random = mulberry32(0x9e3779b9);
    this.load(level);
  }

  load(level: LevelDefinition): void {
    this.level = level;
    if (this.fluid.capacity < level.fluid.amount + FLUID_HEADROOM) {
      this.fluid = new Ferrofluid(level.fluid.amount + FLUID_HEADROOM);
    }
    this.colliders = [...level.walls, ...worldBounds()];
    this.ball = level.ball ? new Ball(level.ball.x, level.ball.y, level.ball.radius) : null;
    this.reset();
  }

  /** Back to planning with the board cleared of magnets. */
  reset(): void {
    this.field.magnets.length = 0;
    this.magnetsUsed = 0;
    this.rewind();
  }

  /**
   * Back to planning with the magnets left where they are.
   *
   * This is the loop the game is actually played in: run it, watch where the fluid went,
   * nudge one magnet, run it again. Clearing the board every time would make iterating on
   * a near-miss more work than starting over.
   */
  rewind(): void {
    this.phase = 'planning';
    this.elapsed = 0;
    this.goalDwell = 0;
    this.settleTime = 0;
    this.goalProgress = 0;
    this.events.length = 0;
    this.random = mulberry32(0x9e3779b9);

    if (this.ball && this.level.ball) {
      this.ball.radius = this.level.ball.radius;
      this.ball.reset(this.level.ball.x, this.level.ball.y);
    }

    this.fill();
  }

  /** Commit the current magnet layout and let it play out. */
  start(): void {
    if (this.phase !== 'planning') return;
    this.phase = 'running';
    this.elapsed = 0;
    this.events.push({ type: 'started' });
  }

  get isRunning(): boolean {
    return this.phase === 'running';
  }

  get isPlanning(): boolean {
    return this.phase === 'planning';
  }

  get solved(): boolean {
    return this.phase === 'solved';
  }

  // --- magnets -------------------------------------------------------------

  get magnetsRemaining(): number {
    return Math.max(0, this.level.magnetsAvailable - this.field.magnets.length);
  }

  /** Magnets can only be touched while planning; a run is committed to as a whole. */
  canEditMagnets(): boolean {
    return this.phase === 'planning';
  }

  canPlaceMagnet(): boolean {
    return this.canEditMagnets() && this.magnetsRemaining > 0 && this.field.magnets.length < MAX_MAGNETS;
  }

  addMagnet(x: number, y: number, size: MagnetSize = 'M', polarity: Polarity = 1): Magnet | null {
    if (!this.canPlaceMagnet()) return null;
    const magnet = new Magnet(clamp(x, 0, WORLD_WIDTH), clamp(y, 0, WORLD_HEIGHT), polarity, size);
    this.field.magnets.push(magnet);
    this.magnetsUsed++;
    return magnet;
  }

  removeMagnet(magnet: Magnet): void {
    if (!this.canEditMagnets()) return;
    const index = this.field.magnets.indexOf(magnet);
    if (index >= 0) this.field.magnets.splice(index, 1);
  }

  moveMagnet(magnet: Magnet, x: number, y: number): void {
    if (!this.canEditMagnets()) return;
    magnet.position.x = clamp(x, 0, WORLD_WIDTH);
    magnet.position.y = clamp(y, 0, WORLD_HEIGHT);
  }

  /** Topmost magnet whose touch radius contains the point, or null. */
  magnetAt(x: number, y: number): Magnet | null {
    if (!this.canEditMagnets()) return null;
    const magnets = this.field.magnets;
    let best: Magnet | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = magnets.length - 1; i >= 0; i--) {
      const magnet = magnets[i];
      const dx = magnet.position.x - x;
      const dy = magnet.position.y - y;
      const distanceSq = dx * dx + dy * dy;
      const hit = magnet.hitRadius();
      if (distanceSq <= hit * hit && distanceSq < bestDistance) {
        best = magnet;
        bestDistance = distanceSq;
      }
    }
    return best;
  }

  // --- filling the reservoir -----------------------------------------------

  /**
   * Vogel spiral: the nth particle goes at angle n * golden angle and radius proportional
   * to sqrt(n). It is the arrangement sunflower seeds use, and it packs evenly at every
   * count — so the reservoir can be filled in one go, at rest, with no settling needed.
   */
  private fillPoint(index: number, out: { x: number; y: number }): void {
    const { source, radius } = this.level.fluid;
    const spacing = PHYSICS.particleRestDistance;
    const distance = Math.max(radius * 0.35, spacing * 0.62 * Math.sqrt(index + 0.5));
    const angle = index * GOLDEN_ANGLE;
    out.x = source.x + Math.cos(angle) * distance;
    out.y = source.y + Math.sin(angle) * distance;
  }

  /** True when a particle placed here would already be inside a wall. */
  private blocked(x: number, y: number): boolean {
    for (const collider of this.colliders) {
      if (collideCircle(collider, x, y, PHYSICS.particleRadius)) return true;
    }
    return false;
  }

  /**
   * True when a wall stands between the nozzle and this point.
   *
   * The reservoir is fluid that has spread from the source, so it can only occupy space it
   * could actually have flowed into. Without this the spiral runs past the arena walls,
   * finds open floor on the far side, and quietly deposits blobs outside the level.
   */
  private outOfSight(x: number, y: number): boolean {
    const { source } = this.level.fluid;
    const dx = x - source.x;
    const dy = y - source.y;
    const steps = Math.ceil(Math.hypot(dx, dy) / (PHYSICS.particleRadius * 2));
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      if (this.blocked(source.x + dx * t, source.y + dy * t)) return true;
    }
    return false;
  }

  /** Place the level's whole fluid budget at the source, at rest. */
  private fill(): void {
    this.fluid.reset();

    const amount = this.level.fluid.amount;
    let index = 0;

    for (let placed = 0; placed < amount; placed++) {
      let ok = false;
      for (let attempt = 0; attempt < EMISSION_ATTEMPTS; attempt++) {
        this.fillPoint(index++, this.fillScratch);

        // A hair of jitter so the spiral does not show through as a visible pattern.
        const jitter = this.level.fluid.radius * 0.06;
        const x = this.fillScratch.x + (this.random() - 0.5) * jitter;
        const y = this.fillScratch.y + (this.random() - 0.5) * jitter;
        if (this.blocked(x, y) || this.outOfSight(x, y)) continue;

        if (!this.fluid.spawn(x, y, 0, 0)) return;
        ok = true;
        break;
      }
      // The source is walled in with nowhere left to put anything. Stop rather than spin.
      if (!ok) return;
    }
  }

  // --- tick ----------------------------------------------------------------

  step(dt: number): void {
    if (this.phase !== 'running') return;
    this.elapsed += dt;

    const ball = this.ball;
    const probe = ball ? this.probe : null;
    if (ball && probe) {
      probe.x = ball.position.x;
      probe.y = ball.position.y;
      probe.radius = ball.radius;
    }

    this.fluid.step(dt, this.field, this.colliders, probe);
    if (ball) {
      ball.step(dt, this.colliders, probe, this.field);
      if (ball.impactSpeed > IMPACT_THRESHOLD) {
        this.events.push({
          type: 'ballImpact',
          x: ball.position.x,
          y: ball.position.y,
          speed: ball.impactSpeed,
        });
      }
    }

    this.evaluate(dt);
  }

  /** How much of what the goal wants is currently in it, 0..1. */
  private measureGoal(): number {
    const goal = this.level.goal;

    if (goal.type === 'ball') {
      if (!this.ball) return 0;
      const dx = this.ball.position.x - goal.x;
      const dy = this.ball.position.y - goal.y;
      return dx * dx + dy * dy <= goal.radius * goal.radius ? 1 : 0;
    }

    const { px, py, count } = this.fluid;
    if (count === 0) return 0;
    const radiusSq = goal.radius * goal.radius;
    let inside = 0;
    for (let i = 0; i < count; i++) {
      const dx = px[i] - goal.x;
      const dy = py[i] - goal.y;
      if (dx * dx + dy * dy <= radiusSq) inside++;
    }
    const required = (goal.fraction ?? 0.5) * count;
    return Math.min(1, inside / Math.max(required, 1));
  }

  /** Decide whether the run has been won, lost, or is still going. */
  private evaluate(dt: number): void {
    const goal = this.level.goal;
    this.goalProgress = this.measureGoal();

    if (this.goalProgress >= 1) {
      // Ball goals need a moment's dwell so a bounce through does not count; fluid goals
      // are already a volume measurement and need no extra patience.
      this.goalDwell += dt;
      if (goal.type === 'fluid' || this.goalDwell >= GOAL_DWELL) {
        this.phase = 'solved';
        this.events.push({ type: 'solved', x: goal.x, y: goal.y });
        return;
      }
    } else {
      this.goalDwell = 0;
    }

    // A run ends when the board stops moving. Everything starts at rest, so nothing is
    // judged until it has had a moment to get going.
    const ballSpeed = this.ball ? Math.hypot(this.ball.velocity.x, this.ball.velocity.y) : 0;
    const moving = Math.max(this.fluid.meanSpeed, ballSpeed);
    if (this.elapsed > SETTLE_GRACE && moving < SETTLE_SPEED) {
      this.settleTime += dt;
    } else {
      this.settleTime = 0;
    }

    if (this.settleTime >= SETTLE_TIME || this.elapsed >= RUN_TIMEOUT) {
      this.phase = 'failed';
      this.events.push({ type: 'failed', x: goal.x, y: goal.y });
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
